import { handleActions } from "redux-actions";
import {
    WHITEBOARD_BOARD_FETCH_COMPLETED,
    WHITEBOARD_COMPONENT_COPY,
    WHITEBOARD_COMPONENT_DRAGGING_STATE,
    WHITEBOARD_COMPONENT_REMOVE_COMPLETED,
    WHITEBOARD_COMPONENT_RESIZE_STATE,
    WHITEBOARD_COMPONENT_SET_ID,
    WHITEBOARD_COMPONENTS_REFRESH,
    WHITEBOARD_COMPONENTS_REFRESH_COMPLETED,
    WHITEBOARD_EXPORT_PDF_ACKNOWLEDGE,
    WHITEBOARD_EXPORT_PDF_COMPLETED,
    WHITEBOARD_EXPORT_PDF_SUBMIT,
    WHITEBOARD_EXPORT_PDF_SUBMITTED,
    WHITEBOARD_FILTER_BY_WORKING_ORDER,
    WHITEBOARD_HISTORY_CLEAR,
    WHITEBOARD_HISTORY_PUSH,
    WHITEBOARD_HISTORY_UPDATE,
    WHITEBOARD_PAGE_CLEAR,
    WHITEBOARD_PAGE_FETCH,
    WHITEBOARD_PAGE_FETCH_COMPLETED,
    WHITEBOARD_PAGE_SET,
    WHITEBOARD_PAGE_SYNC,
    WHITEBOARD_PAGES_CREATE,
    WHITEBOARD_PAGES_SYNC,
    WHITEBOARD_PRODUCT_IMAGE_CHANGE,
    WHITEBOARD_PRODUCT_IMAGE_CHANGE_COMPLETE,
    WHITEBOARD_PRODUCTS_CLEAR,
    WHITEBOARD_PRODUCTS_FETCH,
    WHITEBOARD_PRODUCTS_FETCH_COMPLETED,
    WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE,
    WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE_COMPLETED,
    WHITEBOARD_PRODUCTS_PANEL_CLEAR,
    WHITEBOARD_RESET,
    WHITEBOARD_SCALE,
    WHITEBOARD_SEARCH_CHANGE,
    WHITEBOARD_SYNC,
    WHITEBOARD_TOGGLE_ENFORCE_ASPECT_RATIO,
    WHITEBOARD_TOGGLE_GRID,
    WHITEBOARD_TOGGLE_SHARE_MODE,
} from "constants/action-types";
import { List, Map } from "immutable";
import { formatMessage } from "lib/globalize-util";

// interface IProduct {
//     status: "LOADING" | "LOADED";
//     id?: string;
// }

interface ISyncError {
    objectType: string;
    error: any;
    code: number;
}

interface IState {
    allItemsFetched: boolean;
    boardId: string | null;
    canvasSize: any;
    copiedComponent: Map<string, any> | null;
    currentComponentId: string | null;
    currentPageId: string | null;
    error: any;
    filterByWorkingOrder: boolean;
    isDragging: boolean;
    isResizing: boolean;
    isFetchingProducts?: boolean;
    isRefreshingComponentsLabels: boolean;
    isProductAssetUploading: boolean;
    isFetchingPanelProducts?: boolean;
    isPageFetching: boolean;
    isSearching: boolean;
    isUpdatingOrderFlowType?: boolean;
    orderFlowTypeError?: any;
    generatingPDF: boolean;
    syncError: ISyncError | null;
    pagesId: string | null;
    panelProducts: List<string>;
    pdfUrl: string | null;
    pdfGenerationError: string | null;
    permissions?: List<any>;
    products: List<string>;
    productsNextPage?: string;
    redo: List<any>;
    scale: number;
    searchTerm?: string;
    searchTermForProducts?: string;
    searchTextValue: string;
    shareMode: boolean;
    showGrid: boolean;
    undo: List<any>;
    enforceAspectRatio: boolean;
}

const initialState: IState = {
    allItemsFetched: false,
    boardId: null,
    canvasSize: {
        height: 700,
        width: 1000,
    },
    copiedComponent: null,
    currentComponentId: null,
    currentPageId: null,
    enforceAspectRatio: false,
    error: undefined,
    filterByWorkingOrder: false,
    generatingPDF: false,
    isDragging: false,
    isFetchingPanelProducts: undefined,
    isFetchingProducts: undefined,
    isRefreshingComponentsLabels: false,
    isProductAssetUploading: false,
    isPageFetching: false,
    isResizing: false,
    isSearching: false,
    isUpdatingOrderFlowType: false,
    orderFlowTypeError: null,
    pagesId: null,
    panelProducts: List<string>(),
    pdfUrl: null,
    pdfGenerationError: "",
    permissions: null,
    products: List<string>(),
    productsNextPage: null,
    redo: List(),
    scale: 1,
    searchTerm: "",
    searchTermForProducts: "",
    searchTextValue: "",
    shareMode: false,
    showGrid: true,
    syncError: null,
    undo: List(),
};

export const whiteboard = handleActions(
    {
        [WHITEBOARD_HISTORY_CLEAR]: (state: IState): IState => {
            return {
                ...state,
                redo: List(),
                undo: List(),
            };
        },
        [WHITEBOARD_HISTORY_PUSH]: (state: IState, { payload }: any): IState => {
            const { up, down, key, objectId, mutableType } = payload;
            return {
                ...state,
                redo: state.redo.size ? List() : state.redo,
                undo: state.undo.push({
                    down,
                    key,
                    mutableType,
                    objectId,
                    up,
                }),
            };
        },
        [WHITEBOARD_HISTORY_UPDATE]: (state: IState, { payload: { undo, redo } }: any): IState => {
            return {
                ...state,
                redo,
                undo,
            };
        },
        [WHITEBOARD_SCALE]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                scale: payload,
            };
        },
        [WHITEBOARD_COMPONENT_RESIZE_STATE]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                isResizing: payload,
            };
        },
        [WHITEBOARD_COMPONENT_DRAGGING_STATE]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                isDragging: payload,
            };
        },
        [WHITEBOARD_PAGE_SET]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                currentPageId: payload,
            };
        },
        [WHITEBOARD_COMPONENT_SET_ID]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                currentComponentId: payload,
                searchTerm: "",
                shareMode: false,
            };
        },
        [WHITEBOARD_COMPONENT_REMOVE_COMPLETED]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                currentComponentId: payload,
            };
        },
        [WHITEBOARD_BOARD_FETCH_COMPLETED]: (state: IState, { error, payload }: any): IState => {
            if (error) {
                return {
                    ...state,
                    error: payload,
                };
            }
            return {
                ...state,
                boardId: payload.id,
                pagesId: payload.pages_ref.object_id,
                syncError: null,
            };
        },
        [WHITEBOARD_PAGE_FETCH]: (state: IState): IState => ({
            ...state,
            error: undefined,
            isPageFetching: true,
        }),
        [WHITEBOARD_PAGE_FETCH_COMPLETED]: (state: IState, { payload, error }: any): IState => {
            if (error) {
                return {
                    ...state,
                    error: payload,
                    isPageFetching: false,
                };
            }
            return {
                ...state,
                currentPageId: payload.ref.object_id,
                isPageFetching: false,
                syncError: null,
            };
        },
        [WHITEBOARD_PAGE_CLEAR]: (state: IState): IState => {
            return {
                ...state,
                currentPageId: null,
            };
        },
        [WHITEBOARD_PAGES_CREATE]: (state: IState): IState => {
            return {
                ...state,
                isPageFetching: true,
            };
        },
        [WHITEBOARD_TOGGLE_ENFORCE_ASPECT_RATIO]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                enforceAspectRatio: payload,
            };
        },
        [WHITEBOARD_TOGGLE_GRID]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                showGrid: payload,
            };
        },
        [WHITEBOARD_TOGGLE_SHARE_MODE]: (state: IState, payload: boolean): IState => {
            return {
                ...state,
                currentComponentId: null,
                shareMode: payload === true || payload === false ? payload : !state.shareMode,
            };
        },
        [WHITEBOARD_SEARCH_CHANGE]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                searchTerm: "searchTerm" in payload ? payload.searchTerm : state.searchTerm,
                searchTextValue: "searchTextValue" in payload ? payload.searchTextValue : state.searchTextValue,
            };
        },
        [WHITEBOARD_PRODUCTS_FETCH]: (state: IState, { payload }: any): IState => {
            const isProductFinderFetch = !(payload.filters && "_id" in payload.filters) ? true : undefined;

            return {
                ...state,
                isFetchingPanelProducts: isProductFinderFetch,
                isFetchingProducts: isProductFinderFetch,
                isSearching: !!(payload.filters && "__search" in payload.filters),
                // productsNextPage: null,
            };
        },
        [WHITEBOARD_PRODUCTS_FETCH_COMPLETED]: (state: IState, { payload, error }: any): IState => {
            if (error) {
                return state;
            }

            if ("panelProductResults" in payload) {
                return {
                    ...state,
                    isFetchingPanelProducts: false,
                    isSearching: false,
                    panelProducts: state.panelProducts.push(...payload.panelProductResults),
                };
            }

            return {
                ...state,
                allItemsFetched: payload.results.length < 25, // 25 is currently set limit
                isFetchingProducts: false,
                isSearching: false,
                products: state.products.push(...payload.results),
                productsNextPage: payload.nextPage,
                searchTermForProducts: payload.searchTermForProducts,
            };
        },
        [WHITEBOARD_PRODUCTS_CLEAR]: (state: IState): IState => {
            return {
                ...state,
                allItemsFetched: false,
                isFetchingProducts: undefined,
                products: List<string>(),
                productsNextPage: null,
            };
        },
        [WHITEBOARD_PRODUCTS_PANEL_CLEAR]: (state: IState): IState => {
            return {
                ...state,
                isFetchingPanelProducts: undefined,
                panelProducts: List<string>(),
            };
        },
        [WHITEBOARD_PAGE_SYNC]: (state: IState, { payload, error }: any): IState => {
            if (error) {
                return {
                    ...state,
                    syncError: {
                        code: payload.status || 500,
                        error: payload,
                        objectType: "nuorder.service.board.page",
                    },
                };
            }

            return state;
        },
        [WHITEBOARD_PAGES_SYNC]: (state: IState, { payload, error }: any): IState => {
            if (error) {
                return {
                    ...state,
                    syncError: {
                        code: payload.status || 500,
                        error: payload,
                        objectType: "nuorder.service.board.pages",
                    },
                };
            }

            return state;
        },
        [WHITEBOARD_SYNC]: (state: IState, { payload, error }: any): IState => {
            if (error) {
                return {
                    ...state,
                    syncError: {
                        code: payload.status || 500,
                        error: payload,
                        objectType: "nuorder.service.board",
                    },
                };
            }

            return state;
        },
        [WHITEBOARD_FILTER_BY_WORKING_ORDER]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                filterByWorkingOrder: payload,
            };
        },
        [WHITEBOARD_COMPONENT_COPY]: (state: IState, { payload }: any): IState => {
            return {
                ...state,
                copiedComponent: payload.component,
            };
        },
        [WHITEBOARD_RESET]: (): IState => initialState,
        [WHITEBOARD_EXPORT_PDF_SUBMIT]: (state: IState): IState => {
            return {
                ...state,
                generatingPDF: state.isProductAssetUploading ? false : true,
            };
        },
        [WHITEBOARD_EXPORT_PDF_SUBMITTED]: (state: IState, { error }: any): IState => {
            if (error) {
                return {
                    ...state,
                    generatingPDF: false,
                    pdfGenerationError: formatMessage("PDF have failed to generate. Please try again"),
                };
            }
            return state;
        },
        [WHITEBOARD_EXPORT_PDF_COMPLETED]: (state: IState, { payload, error }: any): IState => {
            if (error || !payload.object_url) {
                let message = formatMessage("PDF have failed to generate. Please try again");
                if (payload.status === "DEADLINE_EXCEEDED") {
                    message = formatMessage("PDF generation has taken too long. Please try again");
                }
                return {
                    ...state,
                    generatingPDF: false,
                    pdfGenerationError: message,
                };
            }
            return {
                ...state,
                generatingPDF: false,
                pdfUrl: payload.object_url,
            };
        },
        [WHITEBOARD_EXPORT_PDF_ACKNOWLEDGE]: (state: IState): IState => {
            return {
                ...state,
                generatingPDF: false,
                pdfGenerationError: null,
                pdfUrl: null,
            };
        },
        [WHITEBOARD_COMPONENTS_REFRESH]: (state: IState): IState => {
            return {
                ...state,
                isRefreshingComponentsLabels: true,
            };
        },
        [WHITEBOARD_COMPONENTS_REFRESH_COMPLETED]: (state: IState): IState => {
            return {
                ...state,
                isRefreshingComponentsLabels: false,
            };
        },
        [WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE]: (state: IState): IState => {
            return {
                ...state,
                isUpdatingOrderFlowType: true,
            };
        },
        [WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE_COMPLETED]: (state: IState, { payload, error }: any): IState => {
            if (error) {
                return {
                    ...state,
                    error: true,
                    isUpdatingOrderFlowType: false,
                    orderFlowTypeError: payload,
                };
            }
            return {
                ...state,
                isUpdatingOrderFlowType: false,
            };
        },
        [WHITEBOARD_PRODUCT_IMAGE_CHANGE]: (state: IState): IState => {
            return {
                ...state,
                isProductAssetUploading: true,
            };
        },
        [WHITEBOARD_PRODUCT_IMAGE_CHANGE_COMPLETE]: (state: IState): IState => {
            return {
                ...state,
                isProductAssetUploading: false,
            };
        },
    } as any,
    initialState,
);
